import { Spinner } from '@components/shared/loader/Spinner';
import { theme } from '@theme';
import { debounce } from 'lodash';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useBrands } from '../../../hooks/Inventory/UseBrands';
import { useExtColor } from '../../../hooks/Inventory/UseExtColor';
import { useIntColor } from '../../../hooks/Inventory/UseIntColor';
import { useModels } from '../../../hooks/Inventory/UseModels';
import { useSetInitialValues } from '../../../hooks/Inventory/UseSetInitialValues';
import { useTrims } from '../../../hooks/Inventory/UseTrims';
import { useVehicleTypes } from '../../../hooks/Inventory/UseVehicleType';
import { useYears } from '../../../hooks/Inventory/UseYears';
import { useFilteredInventoryData } from '../../../hooks/UseFilteredInventoryData';
import { useWindowSizeNoTimeout } from '../../../hooks/UseWindowSizeNoTimeout';
import { useOutsideClickDetection } from '../../../hooks/turn/UseOutsideClickDetection';
import { AppState } from '../../../store/AppState';
import {
  resetReplacementAiMessages,
  setInventorySelectedVehicle,
  setIsInventoryBrowserModalOpen,
  setOfferId,
  setSelectedVehicleId,
} from '../../../store/dealsheet/DealsheetActions';
import { InitialFilterState } from '../../../store/inventory/InitialFilterReducer';
import { InventoryRemove } from '../../../store/inventory/InventoryActions';
import {
  resetFilters,
  setDaysOnLot,
  setMileage,
  setPrice,
  setSearch,
} from '../../../store/inventory/InventoryFilterActions';
import { InventoryFilterState } from '../../../store/inventory/InventoryFilterReducer';
import { InventoryResource } from '../../../types/InvetoryResource';
import { Icon } from '../../style';
import FilterDropdown from './InventoryBrowser/FilterDropdown';
import FilterList from './InventoryBrowser/FilterList';
import VehicleList from './InventoryBrowser/VehicleList';
import FullPageLoader from './sections/FullPageLoader';

export type InventoryBrowserModalProps = {
  data?: any;
  recalculte?: any;
  bulkDataSource?: any;
  onCloseButtonClick: () => void;
};
const InventoryBrowserModal: React.FC<InventoryBrowserModalProps> = (props) => {
  useSetInitialValues();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { count: filteredCount, inventoryData, lastUpdated, loading } = useFilteredInventoryData();

  const dispatch = useDispatch();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const secondWrapperRef = useRef<HTMLDivElement>(null);
  const [windowWidth] = useWindowSizeNoTimeout();

  const [activeFilter, setActiveFilter] = useState('');
  const [amVehicleId, setAmVehicleId] = useState<string | null>(null);
  const {
    label: brandFilterLabel,
    pickedBrands,
    allBrands,
    handleChange: handleBrandsChange,
    countByBrand,
  } = useBrands();
  const {
    label: modelFilterLabel,
    pickedModels,
    allModels,
    handleChange: handleModelChange,
    countByModel,
  } = useModels();
  const {
    label: trimFilterLabel,
    pickedTrims,
    allTrims,
    handleChange: handleTrimChange,
    countByTrim,
  } = useTrims();
  const {
    label: yearFilterLabel,
    allYears,
    pickedYears,
    handleChange: handleYearChange,
    countByYear,
  } = useYears();
  const {
    label: extColorFilterLabel,
    allColorExts,
    pickedColorExts,
    handleChange: handleExtColorChange,
    countByColorExt,
  } = useExtColor();
  const {
    label: intColorFilterLabel,
    allColorInts,
    pickedColorInts,
    handleChange: handleIntColorChange,
    countByColorInt,
  } = useIntColor();
  const {
    label: vehicleTypeFilterLabel,
    allVehicleTypes,
    pickedVehicleTypes,
    handleChange: handleVehicleTypeChange,
    countByVehicleType,
  } = useVehicleTypes();

  const filters = useSelector<AppState, InventoryFilterState>((state) => state.inventoryFilter);

  const redirect = () => {
    const pathParts = location.pathname.split('/');
    const url = `/dealsheet/${props.data?.vin}/${props.data?.dealerId}/offer/0/${props.data?.dealType.toLowerCase()}/${pathParts[7]}${location.search}`;
    dispatch(setOfferId('0'));
    navigate(url);
    window.scrollTo(0, 0);
  };
  const { price, mileage, ageDays } = filters;
  const initialFilter = useSelector<AppState, InitialFilterState>(
    (state) => state.initialInventoryFilter,
  );
  const { price: initialPrice, mileage: initialMileage, ageDays: initialAgeDays } = initialFilter;

  const filtersConfig = [
    { name: 'Vehicle Type', label: vehicleTypeFilterLabel },
    { name: 'Year', label: yearFilterLabel },
    { name: 'Make', label: brandFilterLabel },
    { name: 'Model', label: modelFilterLabel },
    { name: 'Trim', label: trimFilterLabel },
    { name: 'Exterior Color', label: extColorFilterLabel },
    { name: 'Interior Color', label: intColorFilterLabel },
    { name: 'Price', label: getPriceRangeLabel(price) },
    { name: 'Mileage', label: getRangeLabel(mileage) },
    { name: 'Age Days', label: getRangeLabel(ageDays) },
  ];

  function getPriceRangeLabel(price): string {
    if (price.min === -1 && price.max === -1) {
      return '';
    }
    return `$${price.min} - $${price.max}`;
  }
  // get navigate function
  const selectVehicle = (vehicle: InventoryResource) => {
    // construct vehicle
    const tempVehicle: any = {
      rank: 0,
      amVehicleId: vehicle.amVehicleId,
      brandId: vehicle.brandId,
      imageUrl: vehicle.imageUrl,
      isCpo: vehicle.isCpo,
      isVehicleNew: vehicle.isVehicleNew,
      isGhostVehicle: vehicle?.isGhostVehicle,
      model: vehicle.model,
      stockNumber: vehicle.stockNumber,
      trim: vehicle.trim,
      vin: vehicle.vin,
      year: vehicle.year,
      miles: vehicle.miles,
      isAvailableForReplacementCash: vehicle.isAvailableForReplacementCash,
      isAvailableForReplacementFinance: vehicle.isAvailableForReplacementFinance,
      isAvailableForReplacementLease: vehicle.isAvailableForReplacementLease,
      dealerId: vehicle?.dealerId,
    };

    // Construct the payload
    const requestBody: any = {
      dealcalcRequestModel: {
        replacementAmVehicleId: vehicle.amVehicleId,
        replacementVin: vehicle.vin,
        leaseOverrides: null,
        financeOverrides: null,
        cashOverrides: null,
      },
      closedDealId: props.data?.closedDealId,
      isSticky: false,
      isFromTurnDealSheet: false,
      vehicleDealerId: vehicle.dealerId,
      recalculateVehicleInfo: {
        brandId: vehicle?.brandId,
        trim: vehicle?.trim,
        year: vehicle?.year,
        isCpo: vehicle?.isCpo,
        isVehicleNew:
          vehicle?.isVehicleNew === true || vehicle?.isGhostVehicle === true ? true : false,
      },
    };
    let payload: any = {
      tempVehicle: tempVehicle,
      requestBody,
    };

    //making API call
    setIsLoading(true);
    props.recalculte.mutate(
      { payload },
      {
        onSuccess: (result) => {
          dispatch(setIsInventoryBrowserModalOpen(false));
          dispatch(setInventorySelectedVehicle(vehicle));
          setIsLoading(false);
          dispatch(resetFilters());
          dispatch(resetReplacementAiMessages());
          dispatch(setSelectedVehicleId(result?.recalculateDeal?.replacementAmVehicleId));
          setTimeout(() => {
            redirect();
          }, 0);
        },
        onError: () => {
          setIsLoading(false);
          dispatch(setIsInventoryBrowserModalOpen(true));
          dispatch(setInventorySelectedVehicle(null));
        },
      },
    );
  };

  function getRangeLabel(range: { min: number; max: number }): string {
    if (range.min === -1 && range.max === -1) {
      return '';
    }
    return `${range.min} - ${range.max}`;
  }

  const handleFilterClick = (filterName) => {
    setIsVisible(activeFilter === filterName ? !isVisible : true);
    setActiveFilter((prevFilter) => (prevFilter === filterName ? '' : filterName));
  };
  useEffect(() => {
    if (!isVisible) {
      setActiveFilter('');
    }
  }, [isVisible]);

  useOutsideClickDetection(secondWrapperRef, setIsVisible);

  const { search } = useSelector<AppState, InventoryFilterState>((state) => state.inventoryFilter);
  const [searchText, setSearchText] = useState(search);
  const [isSearchClear, setIsSearchClear] = useState<boolean>(true);

  const searchCallback = (searchString) => {
    setSearchText(searchString);
    dispatch(setSearch(searchString));
  };

  const onFilterBoxChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    if (value.length === 0 && !isSearchClear) {
      setIsSearchClear(true);
      searchCallback('');
    } else if (value.length > 2) {
      setIsSearchClear(false);
      searchCallback(e.target.value);
    }
  };
  useEffect(() => {
    if (search === '') {
      if (inputRef.current !== null) {
        inputRef.current.value = '';
      }
    }
  }, [search]);

  const debounceOnFilterChange = useCallback(debounce(onFilterBoxChange, 500), [isSearchClear]);

  const onResetFiltersClick = () => {
    dispatch(resetFilters());
  };

  const onCloseButtoneClicked = () => {
    dispatch(resetFilters());
    dispatch(InventoryRemove());
    dispatch(setIsInventoryBrowserModalOpen(false));
    props.onCloseButtonClick();
  };

  const getDealCount = () => {
    if (props.bulkDataSource && props.bulkDataSource.type === 'bulk-inventory') {
      if (props.bulkDataSource?.isAllRowsSelected) {
        let dealCount = 0;
        props.bulkDataSource.dealerList?.forEach((e) => {
          if (e.dealerId == props.bulkDataSource.selectedDealer[0]) dealCount += e.dealsCount;
        });
        return dealCount;
      } else {
        return props.bulkDataSource.dealerList.length;
      }
    }
    return 0;
  };

  return (
    <ModalWrapper data-testid='inventory-browser-modal'>
      {isLoading && <FullPageLoader text='Recalculating' />}
      <ModalContent windowWidth={windowWidth} data-testid='inventory-browser-modal-content'>
        <InventoryBrowserHeader data-testid='inventory-browser-header'>
          <InventoryBrowserHeaderTitle data-testid='inventory-browser-title'>
            <span>Inventory Browser</span>
            <span> ({filteredCount})</span>
          </InventoryBrowserHeaderTitle>
          <SearchBoxContainer className='search-form' ref={wrapperRef} data-testid='search-deals'>
            <input
              type='text'
              placeholder='Filter by year, make, model, trim, stock #, or VIN...'
              onChange={debounceOnFilterChange}
              ref={inputRef}
              data-testid='search-input'
            />
            <Icon
              type='loupe'
              additionalClassName='am-icon'
              size={theme.typography.h4}
              data-testid='loupe-icon'
            />
          </SearchBoxContainer>
          <ResetFilterButton
            className='btn btn-small btn-white btn-clear'
            onClick={onResetFiltersClick}
          >
            Reset filters
          </ResetFilterButton>
          <CloseButton onClick={() => onCloseButtoneClicked()}>Close</CloseButton>
        </InventoryBrowserHeader>
        <InventoryBrowserDataContainer data-testid='inventory-browser-container'>
          <InventoryBrowserFilters data-testid='inventory-browser-filters' ref={secondWrapperRef}>
            <PrimaryFilters data-testid='inventory-browser-primary-filters'>
              <PrimaryFilterList data-testid='inventory-browser-list'>
                {filtersConfig.map((filter) => (
                  <PrimaryFilterSeciton
                    data-testid='inventory-browser-filter-section'
                    key={filter.name}
                  >
                    <PrimaryFilterName
                      isActive={activeFilter === filter.name}
                      data-testid='inventory-browser-filter-name'
                      onClick={() => handleFilterClick(filter.name)}
                    >
                      <div style={{ display: 'flex' }}>
                        <span>{filter.name}</span>
                        {loading && (
                          <div style={{ height: '20px', marginTop: '3px', paddingLeft: '8px' }}>
                            <Spinner />
                          </div>
                        )}
                      </div>
                    </PrimaryFilterName>
                    <SelectedLabel data-testid='inventory-browser-selected-label'>
                      {filter.label}
                    </SelectedLabel>
                  </PrimaryFilterSeciton>
                ))}
              </PrimaryFilterList>
              <LastUpdatedTag data-testid='inventory-browser-lastupdated-tag'>
                Last updated:
                {loading ? (
                  <div style={{ height: '20px', marginTop: '3px', paddingLeft: '8px' }}>
                    <Spinner />
                  </div>
                ) : (
                  <span style={{ paddingLeft: '8px' }}>{lastUpdated}</span>
                )}
              </LastUpdatedTag>
            </PrimaryFilters>
            {activeFilter && isVisible && (
              <SecondaryFilters data-testid='inventory-browser-secondary-filters'>
                {activeFilter === 'Trim' && (
                  <FilterList
                    allItems={allTrims}
                    pickedItems={pickedTrims}
                    handleChange={handleTrimChange}
                    countByItem={countByTrim}
                  ></FilterList>
                )}
                {activeFilter === 'Make' && (
                  <FilterList
                    allItems={allBrands}
                    pickedItems={pickedBrands}
                    handleChange={handleBrandsChange}
                    data-testid='inventory-browser-filter-list'
                    countByItem={countByBrand}
                  ></FilterList>
                )}
                {activeFilter === 'Model' && (
                  <FilterList
                    allItems={allModels}
                    pickedItems={pickedModels}
                    handleChange={handleModelChange}
                    countByItem={countByModel}
                  ></FilterList>
                )}
                {activeFilter === 'Year' && (
                  <FilterList
                    allItems={allYears}
                    pickedItems={pickedYears}
                    handleChange={handleYearChange}
                    countByItem={countByYear}
                  ></FilterList>
                )}
                {activeFilter === 'Exterior Color' && (
                  <FilterList
                    allItems={allColorExts}
                    pickedItems={pickedColorExts}
                    handleChange={handleExtColorChange}
                    countByItem={countByColorExt}
                  ></FilterList>
                )}
                {activeFilter === 'Interior Color' && (
                  <FilterList
                    allItems={allColorInts}
                    pickedItems={pickedColorInts}
                    handleChange={handleIntColorChange}
                    countByItem={countByColorInt}
                  ></FilterList>
                )}
                {activeFilter === 'Vehicle Type' && (
                  <FilterList
                    allItems={allVehicleTypes}
                    pickedItems={pickedVehicleTypes}
                    handleChange={handleVehicleTypeChange}
                    countByItem={countByVehicleType}
                  ></FilterList>
                )}
                {activeFilter === 'Price' && (
                  <FilterDropdown
                    min={price.min === -1 ? initialPrice.min : price.min}
                    max={price.max === -1 ? initialPrice.max : price.max}
                    minPossibleValue={0}
                    maxPossibleValue={10000000}
                    valueChangeAction={setPrice}
                    initialMin={initialPrice.min}
                    initialMax={initialPrice.max}
                    fontColor='#fff'
                    isPricePrefix={true}
                    testId='price-dropdown'
                  />
                )}
                {activeFilter === 'Mileage' && (
                  <FilterDropdown
                    min={mileage.min === -1 ? initialMileage.min : mileage.min}
                    max={mileage.max === -1 ? initialMileage.max : mileage.max}
                    minPossibleValue={0}
                    maxPossibleValue={10000000}
                    valueChangeAction={setMileage}
                    initialMax={initialMileage.max}
                    initialMin={initialMileage.min}
                    fontColor='#fff'
                    isPricePrefix={false}
                    testId='mileage-dropdown'
                  />
                )}
                {activeFilter === 'Age Days' && (
                  <FilterDropdown
                    min={ageDays.min === -1 ? initialAgeDays.min : ageDays.min}
                    max={ageDays.max === -1 ? initialAgeDays.max : ageDays.max}
                    minPossibleValue={0}
                    maxPossibleValue={10000000}
                    valueChangeAction={setDaysOnLot}
                    initialMax={initialAgeDays.max}
                    initialMin={initialAgeDays.min}
                    fontColor='#fff'
                    isPricePrefix={false}
                    testId='age-days-dropdown'
                  />
                )}
              </SecondaryFilters>
            )}
          </InventoryBrowserFilters>
          <VehicleList
            vehicles={inventoryData}
            loading={loading}
            data-testid='inventory-browser-vehicle-list'
            selectVehicle={selectVehicle}
            bulkData={props.bulkDataSource}
            dealCount={getDealCount()}
          />
        </InventoryBrowserDataContainer>
      </ModalContent>
    </ModalWrapper>
  );
};

const LastUpdatedTag = styled.div`
  display: flex;
  font-size: 14px;
  color: #93a0a9;
  font-weight: 300;
  padding: 10px 15px;
  border-top: 1px solid #3e3e3e;

  > span {
    font-weight: 600;
  }
`;

const SelectedLabel = styled.div`
  font-size: 14px;
  color: #bccbd5;
  line-height: 2;
  padding-left: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  :not(:empty) {
    display: flex;
    align-items: center;
    height: 12px;
    margin-bottom: 8px;
  }

  &&::before {
    content: '•';
    margin-right: 8px;
    font-size: 24px;
    height: 50px;
  }
  :empty::before {
    content: none;
  }
`;

const PrimaryFilterSeciton = styled.div`
  font-size: 16px;
  color: #fff;
  cursor: pointer;
  line-height: 2.5;
  border-bottom: 1px solid #3e3e3e;
  position: relative;
  list-style: none;
  padding: 0 15px 0 15px;
`;

const InventoryBrowserDataContainer = styled.div`
  position: relative;
`;

const InventoryBrowserFilters = styled.div`
  position: relative;
`;

const PrimaryFilters = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: #2d2d2d;
  width: 200px;
  min-height: 600px;
  border-radius: 0 0 0 6px;
`;
const PrimaryFilterList = styled.ul`
  margin: 0;
  padding: 0;
`;
interface PrimaryFilterProps {
  isActive: boolean;
}
const PrimaryFilterName = styled.div<PrimaryFilterProps>`
  &&::after {
    content: '\\E610';
    font-family: icomoon;
    position: absolute;
    right: 15px;
    margin-top: 6px;
    color: ${(props) => (props.isActive ? '#bf8dff;' : '#999')};
    font-size: 72%;
    transform: ${(props) => (props.isActive ? 'rotate(180deg)' : 'rotate(0deg)')};
    top: 0;
  }
`;
const SecondaryFilters = styled.div`
  margin: 0;
  padding: 0;
  background-color: #2d2d2d;
  width: 25%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 200px;
  z-index: 1;
  box-shadow: inset 12px 0 20px rgba(0, 0, 0, 0.2);
  overflow-y: auto;
`;

const CloseButton = styled.button`
  font-size: 14px;
  color: #fff;
  border: 0;
  border-left: 1px solid #653e97;
  opacity: 1;
  text-shadow: none;
  height: 100%;
  bottom: 0;
  right: 0;
  margin-top: 0;
  padding: 0 20px;
  text-transform: uppercase;
  cursor: pointer;
  background: transparent;
  appearance: none;
  font-weight: 600;
  width; 20%;

  &:hover {
    background-color: #412662;
    border-top-right-radius: 6px;
    outline: none;
  }
`;

const ResetFilterButton = styled.button`
  margin-right: 4rem;
`;

const SearchBoxContainer = styled.div`
  flex: 1;
  display: flex;
  height: 30px;
  margin: 15px 20px;
  position: relative;

  > input {
    width: 100%;
    height: 30px;
    color: #b4a5ca;
    font-size: 14px;
    font-weight: 300;
    letter-spacing: 0.05em;
    padding: 0 19px 0 25px;
    box-sizing: border-box;
    border: 1px solid #6a4c90;
    border-bottom-color: #775b9b;
    border-right-color: #775b9b;
    background: #6a4a94;
    outline: none;
    border-radius: 5px;
    transition: all 0.1s ease-in-out;

    &:focus + .am-icon-loupe {
      color: #593487;
    }
    &:focus {
      color: #593487;
      background-color: #dbceec;
      font-weight: 500;
    }
  }

  input::placeholder {
    color: #b4a5ca;
    opacity: 1;
  }

  input::-ms-input-placeholder {
    /* Microsoft Edge */
    color: #b4a5ca;
    opacity: 1;
  }

  .am-icon {
    font-size: 130%;
    position: absolute;
    top: 6px;
    left: 4px;
    color: #b4a5ca;
  }
`;

const InventoryBrowserHeader = styled.div`
  display: flex;
  align-items: center;
  background-color: #55348c;
  position: relative;
  height: 50px;
  padding: 0 0 0 20px;
  border-radius: 6px 6px 0 0;
  border: 0;
`;

const InventoryBrowserHeaderTitle = styled.h4`
  width: 30%;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  line-height: 50px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ModalWrapper = styled.div`
  position: fixed;
  z-index: 300;
  left: 0;
  top: 0;
  margin-top: 0px;
  padding-top: 70px;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgb(0, 0, 0);
  background-color: rgb(0, 0, 0, 0.4);
`;

type ModalContentProps = {
  windowWidth: number;
};

const ModalContent = styled.div<ModalContentProps>`
  background-color: #fff;
  background-clip: padding-box;
  margin: auto;
  border: 1px solid #888;
  width: 80%;

  ${(props) =>
    props.windowWidth < 1000
      ? `
      width: 90%;
    `
      : ``};

  ${(props) =>
    props.windowWidth < 880
      ? `
      width: 100%;
    `
      : ``};

  border-radius: 8px 8px 6px 6px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  max-width: 1000px;
`;

export default InventoryBrowserModal;
